<template>
    <div class="experience-dialog__wrap">
        <div v-if="fullscreen">
            <pt-dialog
                ref="experienceDialogRef"
                center
                :model-value="true"
                :show-close="fullscreen"
                :fullscreen="fullscreen"
                :custom-class="[
                    $style.dialog,
                    'experienceDialog',
                    fullscreen ? $style.fullscreen : '',
                    $style[props.component],
                    props.headIcon ? $style.headIcon : ''
                ]"
                :top="fullscreen ? '0px' : '20px'"
                :width="fullscreen ? '100%' : 'calc(100% - 92px)'"
                @opened="handleOpened"
                @close="handleClose"
            >
                <template #title>
                    <div v-if="props.visibleBack" class="pt-dialog__back" @click="handleBack">
                        <pt-icon
                            icon="pt-icon--back"
                            :style="{
                                width: '18px',
                                height: '18px',
                                fill: '#344563'
                            }"
                        ></pt-icon>
                        <pt-link type="text" color="black" dom-type="a" size="large">
                            <span>{{ t('header.btn_back') }}</span>
                        </pt-link>
                    </div>
                    <pt-icon
                        v-if="!fullscreen"
                        icon="pt-icon-solid-close"
                        class="pt-dialog__outside-close"
                        @click="handleClose"
                    ></pt-icon>
                </template>

                <header>
                    <div v-if="headIcon" :class="$style.icon">
                        <pt-icon
                            :icon="headIcon"
                            :style="{
                                width: '54px',
                                height: '54px'
                            }"
                        ></pt-icon>
                    </div>
                    <div v-if="props.title" :class="$style.title">
                        <span>{{ props.title }}</span>
                    </div>
                </header>
                <slot></slot>
            </pt-dialog>
        </div>
        <div v-else-if="props.component === 'ExperienceForm'">
            <pt-dialog-v2
                :model-value="true"
                :title="props.title"
                :hasFooter="false"
                :iconKey="headIcon"
                :hasScroll="false"
                @close="handleClose"
            >
                <slot></slot>
            </pt-dialog-v2>
        </div>
        <div v-else>
            <pt-drawer :model-value="true" :title="props.title" @close="handleClose">
                <slot></slot>
            </pt-drawer>
        </div>
    </div>
</template>

<script lang="ts">
export default {
    name: 'ExperienceDialog'
};
</script>

<script lang="ts" setup>
import useEventBus from '@assets/plugins/eventBus';
import { ref, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

// interface
interface Props {
    title: string;
    component: string;
    headIcon?: string;
    visibleBack: boolean;
    fullscreen: boolean;
}

// props
const props = withDefaults(defineProps<Props>(), {
    visibleBack: true,
    fullscreen: false
});
// emits
const emit = defineEmits(['close', 'back']);

onUnmounted(() => {
    experienceDialogRef.value?.dialogRef?.removeEventListener('scroll', handleScroll, false);
});

// state
const { t } = useI18n();
const { eventBus } = useEventBus();
const experienceDialogRef = ref();

function handleClose() {
    emit('close');
    experienceDialogRef.value?.dialogRef?.removeEventListener('scroll', handleScroll, false);
}

function handleBack() {
    emit('back');
}

function handleOpened() {
    experienceDialogRef.value.dialogRef.addEventListener('scroll', handleScroll, false);
}

function handleScroll() {
    eventBus.$emit('experienceDialogScroll');
    eventBus.$emit('resizeTip');
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
:global {
    // 当详情弹框出现【无权限弹框】时，需让无权限弹框层级更高
    body:has(.experience-dialog__wrap > .pt-overlay) {
        & > .pt-overlay:not([aria-hidden='true']),
        & > .pt-popper {
            z-index: calc(#{$page-help-index} + 2) !important; // 需要比start guide层级高
        }
    }

    .experience-dialog__wrap {
        z-index: calc(#{$page-help-index} + 1); // 需要比start guide层级高
    }
}

.dialog {
    width: calc(100% - 90px);
    height: calc(100vh - 40px);
    margin-bottom: 0 !important;

    &.ExperienceABTestTypeSelect {
        :global {
            .pt-dialog__body {
                max-width: 90%;
                margin: 0 auto;
            }
        }
    }

    &.ExperienceNonABTestList {
        height: auto;
        :global {
            .pt-dialog__body {
                padding-bottom: 56px !important;
            }
        }
    }

    &.ExperienceTemplateBlank {
        height: auto;

        :global {
            .pt-dialog__footer {
                display: none;
            }
            .pt-dialog__header,
            .pt-dialog__body {
                padding: 0 !important;
            }
        }

        header {
            display: none;
        }
    }

    &.ExperienceForm {
        :global {
            .pt-dialog__body {
                header {
                    padding: 16% 0 56px;
                }
            }
        }

        &.headIcon {
            :global {
                .pt-dialog__body {
                    header {
                        padding: 10% 0 56px;
                    }
                }
            }
        }
    }

    :global {
        .pt-dialog {
            border-radius: 8px;
        }
        .pt-dialog__header {
            padding: 0 48px !important;

            .pt-dialog__back {
                height: 56px;
                padding: 18px 0;
                position: absolute;
                top: 0;
            }

            .pt-dialog__outside-close {
                position: absolute;
                width: 28px;
                height: 28px;
                fill: #b3bac5;
                right: -36px;
                top: 0;
                cursor: pointer;
                transition: 0.15s linear;
                &:hover {
                    fill: #dfe1e6;
                }
            }
        }
        .pt-dialog__body {
            padding: 0 12% !important;
            min-height: 340px;
        }
        .pt-dialog__footer {
            padding: 0 0 64px !important;
        }
    }

    header {
        padding: 48px 0;

        .title {
            font-size: 32px;
            font-weight: normal;
            text-align: center;
        }

        .icon {
            text-align: center;
            margin-bottom: 36px;
        }
    }
}
.header {
    padding: 56px 0;

    .title {
        font-size: 32px;
        font-weight: normal;
        text-align: center;
    }

    .icon {
        text-align: center;
        margin-bottom: 36px;
    }
}

.fullscreen {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;

    :global {
        .pt-dialog {
            border-radius: 0px;
        }
        .pt-dialog__body {
            padding: 0 !important;
        }
        .pt-dialog__footer {
            display: none;
        }
    }
}
</style>
